/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const SITE_ORIGIN = `https://918kiss-official.app`
const OG_IMAGE = `${SITE_ORIGIN}/918kiss-logo.png`

function SEO({
  description,
  lang,
  meta,
  keywords,
  title,
  pagePath,
  noindex,
  schema,
  links,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description

  // Self-canonicalise to the absolute URL for this page (when pagePath given).
  const canonicalUrl = pagePath ? `${SITE_ORIGIN}${pagePath}` : null

  const linkTags =
    canonicalUrl && !noindex ? [{ rel: `canonical`, href: canonicalUrl }] : []

  // Extra <link> tags supplied by the page (e.g. preconnects), appended
  // after the canonical link.
  const allLinkTags = linkTags.concat(links)

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={allLinkTags}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ]
        .concat(
          canonicalUrl && !noindex
            ? {
                property: `og:url`,
                content: canonicalUrl,
              }
            : []
        )
        .concat(
          canonicalUrl && !noindex
            ? {
                property: `og:image`,
                content: OG_IMAGE,
              }
            : []
        )
        .concat(
          noindex
            ? {
                name: `robots`,
                content: `noindex`,
              }
            : []
        )
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `),
              }
            : []
        )
        .concat(meta)}
    >
      {schema.length > 0 && (
        // react-helmet v5: JSON-LD as string children of <script> (the v5
        // serializer ignores dangerouslySetInnerHTML). One @graph block so the
        // page emits exactly one ld+json script.
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": `https://schema.org`,
            "@graph": schema,
          })}
        </script>
      )}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: ``,
  pagePath: ``,
  noindex: false,
  schema: [],
  links: [],
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  pagePath: PropTypes.string,
  noindex: PropTypes.bool,
  schema: PropTypes.arrayOf(PropTypes.object),
  links: PropTypes.arrayOf(PropTypes.object),
}

export default SEO
